import { useCallback, useEffect, useState } from 'react';

import './GuidancePanel.css';

export interface GuidanceStep {
  title: string;
  body: string;
}

export interface GuidanceGlossary {
  term: string;
  meaning: string;
}

export interface GuidancePanelProps {
  /** Unique screen key used for localStorage dismissal persistence. */
  screenKey: string;
  /** Small eyebrow label, e.g. "§ GETTING STARTED". */
  eyebrow: string;
  /** Short headline for the panel. */
  title: string;
  /** One-sentence summary of what the screen does. */
  intro: string;
  /** Who the screen is for — "For validation analysts + managers". */
  audience: string;
  /** Three numbered "how to use it" steps. */
  steps: GuidanceStep[];
  /** Three to five short glossary entries. */
  glossary: GuidanceGlossary[];
}

const STORAGE_PREFIX = 'sentinel.guidance.';

function storageKey(screenKey: string): string {
  return `${STORAGE_PREFIX}${screenKey}.dismissed`;
}

function readDismissed(screenKey: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(storageKey(screenKey)) === '1';
  } catch {
    return false;
  }
}

function writeDismissed(screenKey: string, dismissed: boolean) {
  if (typeof window === 'undefined') return;
  try {
    if (dismissed) window.localStorage.setItem(storageKey(screenKey), '1');
    else window.localStorage.removeItem(storageKey(screenKey));
  } catch {
    // private-mode / storage-disabled — non-fatal for the panel
  }
}

/**
 * Dismissible onboarding/guidance card rendered just below each screen's
 * PageHead. Persists the dismissal in localStorage so power users don't see
 * the panel on every visit. Dismissed state collapses to a small "Show
 * guidance" button.
 */
export default function GuidancePanel({
  screenKey,
  eyebrow,
  title,
  intro,
  audience,
  steps,
  glossary,
}: GuidancePanelProps) {
  const [dismissed, setDismissed] = useState<boolean>(() => readDismissed(screenKey));

  // If the user clears localStorage in another tab the panel re-appears on
  // the next focus — keeps the visibility in sync.
  useEffect(() => {
    const onFocus = () => setDismissed(readDismissed(screenKey));
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [screenKey]);

  const dismiss = useCallback(() => {
    setDismissed(true);
    writeDismissed(screenKey, true);
  }, [screenKey]);

  const reopen = useCallback(() => {
    setDismissed(false);
    writeDismissed(screenKey, false);
  }, [screenKey]);

  if (dismissed) {
    return (
      <div className="guidance-panel-reopen" data-testid={`guidance-reopen-${screenKey}`}>
        <button
          type="button"
          className="btn btn-ghost btn-sm guidance-panel-reopen__btn"
          onClick={reopen}
          title="Re-open the getting-started guidance for this screen"
          aria-label={`Show guidance for ${screenKey}`}
        >
          Show guidance
        </button>
      </div>
    );
  }

  return (
    <section
      className="guidance-panel"
      data-testid={`guidance-panel-${screenKey}`}
      aria-label={`Getting started · ${title}`}
    >
      <header className="guidance-panel__head">
        <div>
          <div className="eyebrow guidance-panel__eyebrow">{eyebrow}</div>
          <h2 className="guidance-panel__title">{title}</h2>
          <p className="guidance-panel__intro">{intro}</p>
          <p className="guidance-panel__audience mono">{audience}</p>
        </div>
        <button
          type="button"
          className="guidance-panel__dismiss"
          onClick={dismiss}
          title="Dismiss this guidance panel — you can re-open it later."
          aria-label="Dismiss guidance"
          data-testid={`guidance-dismiss-${screenKey}`}
        >
          Dismiss ×
        </button>
      </header>

      <div className="guidance-panel__body">
        <ol className="guidance-panel__steps" aria-label="How to use this screen">
          {steps.map((step, idx) => (
            <li key={`${step.title}-${idx}`} className="guidance-panel__step">
              <span className="guidance-panel__step-num mono">{idx + 1}</span>
              <div>
                <strong className="guidance-panel__step-title">{step.title}</strong>
                <p className="guidance-panel__step-body">{step.body}</p>
              </div>
            </li>
          ))}
        </ol>

        <dl className="guidance-panel__glossary" aria-label="Key terms">
          {glossary.map((entry) => (
            <div key={entry.term} className="guidance-panel__glossary-row">
              <dt className="guidance-panel__term mono">{entry.term}</dt>
              <dd className="guidance-panel__meaning">{entry.meaning}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}
